import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SendIcon from "@iconify/icons-mdi/send";
import { InlineIcon } from "@iconify/react";
import { MY_HR_CREATE_CHANGE_MESSAGE } from "my-hr/graphql";
import { useMyHrChangeRequests } from "my-hr/hooks";
import { useOcCurrentUserState } from "oc/hooks";

interface MyHrNewMessageProps {
  loading: boolean;
  selectedRequest: string | number; // Adjust type as needed
  disabled?: boolean;
  type: string;
  typeGroupId?: string | number; // Adjust type as needed
  typeGroup?: boolean | string;
}

export const MyHrNewMessage: React.FC<MyHrNewMessageProps> = ({
  loading,
  selectedRequest,
  disabled,
  type,
  typeGroupId,
  typeGroup,
}) => {
  const { employeeCode } = useOcCurrentUserState();
  const { refetch } = useMyHrChangeRequests(employeeCode);
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [newMessageText, setNewMessageText] = useState("");
  const [newMessageError, setNewMessageError] = useState(false);

  const [createChangeMessage] = useMutation(MY_HR_CREATE_CHANGE_MESSAGE, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.log({ error });
    },
  });

  const handleSubmitNewMessage = () => {
    if (newMessageText === "") {
      setNewMessageError(true);
      return;
    }

    let variables = {
      request: selectedRequest,
      message: newMessageText,
      locale: i18n.language,
      createdBy: employeeCode,
      type: t(type),
    };

    if (typeGroup && typeGroupId !== undefined) {
      variables.request = typeGroupId;
    }

    createChangeMessage({
      variables: variables,
    });

    setNewMessageText("");
    setNewMessageError(false);
  };

  useEffect(() => {
    return () => {
      setNewMessageText("");
      setNewMessageError(false);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <TextField
        multiline={newMessageText?.length > 50 ? true : false}
        fullWidth
        variant="outlined"
        placeholder={t("OC_WRITE_MESSAGE")}
        value={newMessageText}
        onChange={(e) => setNewMessageText(e.target.value)}
        onFocus={() => setNewMessageError(false)}
        error={newMessageError}
        helperText={
          newMessageError
            ? `${t("OC_EMPTY_MESSAGE")}`
            : `${newMessageText.length}/255`
        }
        style={{
          height: "auto",
        }}
        disabled={loading || disabled}
        inputProps={{ maxLength: 255 }}
        FormHelperTextProps={
          newMessageError
            ? { style: { textAlign: "left" } }
            : { style: { textAlign: "right" } }
        }
        InputProps={{
          style: {
            backgroundColor: "white",
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                id="my-hr-new-message-button"
                onClick={handleSubmitNewMessage}
                style={{
                  backgroundColor: theme.palette.gray20.main,
                  height: "48px",
                  width: "48px",
                }}
              >
                <InlineIcon
                  icon={SendIcon}
                  height="24px"
                  color={theme.palette.gray60.main}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />{" "}
    </>
  );
};

export default MyHrNewMessage;
